/**
 * Central state for data collected across the onboarding flow.
 * Flat key-value store; use stateKey like "businessName" with OnboardingTextInput.
 */

import { match, P } from 'ts-pattern';

export type OnboardingState = Record<string, string | boolean | string[]>;

export const initialOnboardingState: OnboardingState = {
  businessLegalName: '',
  businessPhone: '',
  businessDba: '',
  businessAddress: '',
  businessEin: '',
  businessDescription: '',
  businessNaics: '',
  businessWebsite: '',
  businessStateOfIncorporation: '',
  businessYearIncorporated: '',
  businessOwnershipType: '',
  businessIsRegulatedIndustry: false,
  businessStrategicPartners: '',
  businessHoldFinancialLicenses: false,
  businessHasUndergoneChanges: false,
  businessChanges: '',
  businessOwnershipHeldByTrust: false,
  businessIndustryVerticals: '',
  businessMarketplaceTransactions: '',
  businessSellersAndBuyersConnection: '',
  businessSellersAsIntermediares: false,
  businessCountriesOfOperation: [] as string[],
  businessAllowsCrossBorder: false,
  businessHadTerminatedProcessorRelationship: false,
};

export type OnboardingAction =
  | { type: 'SET_FIELD'; stateKey: string; value: string | string[] | boolean }
  | { type: 'RESET' };

export const onboardingReducer = (
  state: OnboardingState,
  action: OnboardingAction
): OnboardingState => {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.stateKey]: action.value };
    case 'RESET':
      return { ...initialOnboardingState };
    default:
      return state;
  }
};

const requiredStateKeys = Object.keys(initialOnboardingState);

/**
 * Returns true if every key has a non-empty trimmed value.
 * Pass `keys` to validate only a subset of fields.
 */
export const areAllFieldsFilled = (
  state: OnboardingState,
  keys: string[] = requiredStateKeys
): boolean =>
  keys.every((key) =>
    match(state[key])
      .with(P.array(P.string), (arr) => arr.every((k) => k.trim().length > 0))
      .with(P.boolean, () => true)
      .with(P.string, (str) => str.trim().length > 0)
      .exhaustive()
  );
